import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import {
  Ruler, Pencil, Route, LocateFixed, FileText, Cloud,
  MapPinned, Layers, Compass, Camera, Download, Share2, ShieldCheck, Smartphone
} from "lucide-react";
import { FEATURES } from "../data/mock";

const iconMap = { Ruler, Pencil, Route, LocateFixed, FileText, Cloud };

const extra = [
  { icon: Layers, title: "Custom Layers", desc: "Toggle survey, satellite, topo and custom KML overlays on the fly." },
  { icon: Compass, title: "Digital Compass", desc: "True-north bearing with declination correction for traverse work." },
  { icon: Camera, title: "Geotagged Photos", desc: "Attach photos and notes to any pin, exported with your shapefile." },
  { icon: Download, title: "Offline Tiles", desc: "Pre-cache Esri World Imagery, OSM and topo tiles for remote sites." },
  { icon: Share2, title: "Crew Sharing", desc: "Share map packages and tracks across your crew with one tap." },
  { icon: ShieldCheck, title: "Enterprise Security", desc: "SOC2-aligned storage, SSO and audit logs for regulated industries." }
];

export default function Features() {
  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
      <div className="max-w-3xl">
        <Badge className="bg-emerald-50 text-emerald-800 hover:bg-emerald-50 border-emerald-200 mb-3">Features</Badge>
        <h1 className="text-4xl lg:text-5xl font-extrabold text-stone-900 tracking-tight">Built end-to-end for field work.</h1>
        <p className="mt-4 text-lg text-stone-600 leading-relaxed">
          Every tool a forester, lineman or environmental tech needs — measuring, drawing, exporting and locating — wrapped around your georeferenced PDF maps.
        </p>
      </div>

      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {FEATURES.map((f) => {
          const Icon = iconMap[f.icon] || MapPinned;
          return (
            <Card key={f.id} className="p-6 border-stone-200 hover:border-emerald-300 hover:shadow-md transition bg-white">
              <div className="h-12 w-12 rounded-xl bg-emerald-700 text-white flex items-center justify-center">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-stone-900">{f.title}</h3>
              <p className="mt-1.5 text-sm text-stone-600 leading-relaxed">{f.description}</p>
            </Card>
          );
        })}
      </div>

      <div className="mt-16">
        <h2 className="text-2xl lg:text-3xl font-bold text-stone-900">And a whole lot more</h2>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {extra.map((e, i) => (
            <Card key={i} className="p-5 border-stone-200 bg-stone-50">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-lg bg-white border border-stone-200 text-emerald-700 flex items-center justify-center shrink-0">
                  <e.icon className="h-4 w-4" />
                </div>
                <div>
                  <div className="font-semibold text-stone-900">{e.title}</div>
                  <div className="text-sm text-stone-600 mt-0.5">{e.desc}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <Card className="mt-16 p-8 lg:p-12 border-stone-200 bg-stone-900 text-white overflow-hidden relative">
        <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-emerald-700/30 blur-3xl" />
        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div>
            <Smartphone className="h-8 w-8 text-emerald-400 mb-3" />
            <h3 className="text-2xl lg:text-3xl font-bold">Field-ready on day one.</h3>
            <p className="mt-2 text-stone-300 max-w-xl">Open your PDF maps offline, drop pins, measure, draw — then sync everything when you’re back in coverage.</p>
          </div>
          <Link to="/dashboard">
            <Button size="lg" className="bg-emerald-600 hover:bg-emerald-500 text-white gap-2 h-12 px-6">
              Try it now <MapPinned className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}
